import React from 'react';
import { getHolidaysForDate } from '../../../utils/holidayService';

const getActivityStyle = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const personColors = activity?.personColors || (activity?.personColor ? [activity?.personColor] : []);
  
  if (personIds?.length === 0 || personColors?.length === 0) {
    return {
      backgroundColor: '#e5e7eb20',
      borderLeft: '3px solid #e5e7eb'
    };
  }
  
  // Check if all members are selected
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected) {
    // Rainbow gradient for all members
    return {
      background: 'linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6, #6366f1, #a855f7)',
      borderLeft: '3px solid #a855f7',
      backgroundSize: '200% 100%',
      animation: 'rainbow-shift 3s ease infinite'
    };
  } else if (personColors?.length === 1) {
    // Single color
    return {
      backgroundColor: `${personColors?.[0]}20`,
      borderLeft: `3px solid ${personColors?.[0]}`
    };
  } else {
    // Gradient blend of selected colors
    const gradientStops = personColors?.map((color, idx) => {
      const position = (idx / (personColors?.length - 1)) * 100;
      return `${color} ${position}%`;
    })?.join(', ');
    
    return {
      background: `linear-gradient(90deg, ${gradientStops})`,
      borderLeft: `3px solid ${personColors?.[0]}`,
      opacity: 0.9
    };
  }
};

const getActivityTextColor = (activity, persons) => {
  const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
  const allIds = persons?.map(p => p?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
  
  if (allSelected || (activity?.personColors?.length > 1)) {
    return '#ffffff';
  }
  
  return activity?.personColor || activity?.personColors?.[0] || '#000000';
};

const CalendarGrid = ({ 
  currentDate, 
  activities, 
  onActivityClick,
  onDateClick,
  onDragStart,
  onDragOver,
  onDrop,
  preferences,
  persons = []
}) => {
  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  
  const isToday = (day) => {
    const today = new Date();
    return today?.getDate() === day && 
           today?.getMonth() === month && 
           today?.getFullYear() === year;
  };

  const getActivitiesForDate = (day) => {
    return activities?.filter(activity => {
      const activityDate = new Date(activity.date);
      return activityDate?.getDate() === day && 
             activityDate?.getMonth() === month && 
             activityDate?.getFullYear() === year;
    });
  };

  const getHolidaysForDay = (day) => {
    const date = new Date(year, month, day);
    return getHolidaysForDate(
      date,
      preferences?.holidayLocation,
      preferences?.showPublicHolidays,
      preferences?.showSchoolTerms
    );
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarDays = [];
  
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays?.push(null);
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays?.push(day);
  }

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.add('bg-muted');
  };

  const handleDragLeave = (e) => {
    e?.currentTarget?.classList?.remove('bg-muted');
  };

  const handleDrop = (e, day) => {
    e?.preventDefault();
    e?.currentTarget?.classList?.remove('bg-muted');
    const newDate = new Date(year, month, day);
    onDrop(e, newDate);
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm-md overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border bg-muted/50">
        {weekDays?.map((day) => (
          <div 
            key={day}
            className="p-3 md:p-4 text-center text-xs md:text-sm font-semibold text-muted-foreground"
          >
            <span className="hidden md:inline">{day}</span>
            <span className="md:hidden">{day?.slice(0, 1)}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {calendarDays?.map((day, index) => {
          const dayActivities = day ? getActivitiesForDate(day) : [];
          const dayHolidays = day ? getHolidaysForDay(day) : [];
          const isTodayDate = day && isToday(day);
          
          return (
            <div
              key={index}
              className={`min-h-[80px] md:min-h-[120px] lg:min-h-[140px] border-r border-b border-border p-1 md:p-2 transition-colors ${
                day ? 'hover:bg-muted/50 cursor-pointer' : 'bg-muted/20'
              } ${isTodayDate ? 'bg-primary/5' : ''}`}
              onClick={() => day && onDateClick(new Date(year, month, day))}
              onDragOver={day ? handleDragOver : undefined}
              onDragLeave={day ? handleDragLeave : undefined}
              onDrop={day ? (e) => handleDrop(e, day) : undefined}
            >
              {day && (
                <>
                  <div className={`text-xs md:text-sm font-medium mb-1 md:mb-2 ${
                    isTodayDate ? 'text-primary font-bold' : 'text-foreground'
                  }`}>
                    {isTodayDate && (
                      <span className="inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full bg-primary text-primary-foreground text-xs md:text-sm font-bold">
                        {day}
                      </span>
                    )}
                    {!isTodayDate && day}
                  </div>

                  {/* Holiday Indicators */}
                  {dayHolidays?.length > 0 && (
                    <div className="space-y-0.5 mb-1">
                      {dayHolidays?.map((holiday, idx) => (
                        <div
                          key={idx}
                          className={`text-[10px] md:text-xs px-1 py-0.5 rounded truncate ${
                            holiday?.type === 'public' ?'bg-red-100 text-red-700 border border-red-200' 
                              : holiday?.type === 'school' ?'bg-blue-100 text-blue-700 border border-blue-200' :'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          }`}
                          title={holiday?.name}
                        >
                          {holiday?.name}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Activity Indicators */}
                  <div className="space-y-1">
                    {dayActivities?.slice(0, 3)?.map((activity) => {
                      const activityStyle = getActivityStyle(activity, persons);
                      const textColor = getActivityTextColor(activity, persons);
                      const personIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
                      const allIds = persons?.map(p => p?.id) || [];
                      const allSelected = allIds?.length > 0 && allIds?.every(id => personIds?.includes(id));
                      
                      return (
                        <div
                          key={activity?.id}
                          className="text-xs p-1 md:p-1.5 rounded cursor-move transition-transform hover:scale-105"
                          style={activityStyle}
                          draggable
                          onDragStart={(e) => onDragStart(e, activity)}
                          onClick={(e) => {
                            e?.stopPropagation();
                            onActivityClick(activity);
                          }}
                        >
                          <div className="font-medium truncate flex items-center gap-1" style={{ color: textColor }}>
                            {activity?.title}
                            {allSelected && <span className="text-[10px]">✨</span>}
                          </div>
                          <div className="text-[10px] truncate" style={{ color: textColor, opacity: 0.8 }}>
                            {activity?.time}
                          </div>
                        </div>
                      );
                    })}
                    
                    {dayActivities?.length > 3 && (
                      <div className="text-[10px] text-muted-foreground text-center py-1">
                        +{dayActivities?.length - 3} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;